/* Desktop: steps are a tablist on the left, one shared panel area on the right.
   Mobile: the same steps become an accordion -- each panel is moved in right
   after its own step button, so opening a step collapses the previous one in
   place instead of swapping content further down the page. */
const PROCESS_STEPS_MOBILE = '(max-width: 989px)';

class ProcessSteps extends HTMLElement {
  constructor() {
    super();
    this.list = this.querySelector('[role="tablist"]');
    this.panelsContainer = this.querySelector('[data-step-panels]');
    this.buttons = Array.from(this.querySelectorAll('[data-step-button]'));
    this.panels = Array.from(this.querySelectorAll('[data-step-panel]'));
    this.mobileQuery = window.matchMedia(PROCESS_STEPS_MOBILE);
    this.onLayoutChange = this.applyLayout.bind(this);

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  connectedCallback() {
    this.applyLayout();
    this.mobileQuery.addEventListener('change', this.onLayoutChange);
  }

  disconnectedCallback() {
    this.mobileQuery.removeEventListener('change', this.onLayoutChange);
  }

  get isMobile() {
    return this.mobileQuery.matches;
  }

  applyLayout() {
    if (this.isMobile) {
      this.panels.forEach((panel) => {
        const button = this.buttonFor(panel.getAttribute('data-step-panel'));
        if (button) button.after(panel);
        // No tablist on mobile, so the panel is a plain labelled region.
        panel.setAttribute('role', 'region');
      });
      if (this.panelsContainer) this.panelsContainer.hidden = true;
      if (this.list) this.list.removeAttribute('role');
    } else {
      this.panels.forEach((panel) => {
        if (this.panelsContainer) this.panelsContainer.appendChild(panel);
        panel.setAttribute('role', 'tabpanel');
      });
      if (this.panelsContainer) this.panelsContainer.hidden = false;
      if (this.list) this.list.setAttribute('role', 'tablist');
    }

    const active = this.buttons.find((button) => button.classList.contains('process-steps__step--active'));
    this.selectStep(active ? active.getAttribute('data-step-button') : '1');
  }

  buttonFor(index) {
    return this.buttons.find((button) => button.getAttribute('data-step-button') === index);
  }

  onButtonClick(event) {
    this.selectStep(event.currentTarget.getAttribute('data-step-button'));
  }

  selectStep(index) {
    const { isMobile } = this;

    this.buttons.forEach((button) => {
      const isActive = button.getAttribute('data-step-button') === index;
      button.classList.toggle('process-steps__step--active', isActive);

      // A tab exposes selection, an accordion header exposes expansion --
      // keep exactly one of the two on the button per layout.
      if (isMobile) {
        button.removeAttribute('role');
        button.removeAttribute('aria-selected');
        button.setAttribute('aria-expanded', isActive);
      } else {
        button.removeAttribute('aria-expanded');
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', isActive);
      }
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-step-panel') !== index;
    });
  }
}

customElements.define('process-steps', ProcessSteps);
